import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import * as uuid from 'uuid'
import { State } from 'store'
import { getIsPartnerAdmin } from 'auth/selectors'
import { GoogleTTDServiceContext } from 'channels/context'
import { ImagesServiceContext } from 'http/context'
import { retrieveImgDimentions } from 'uiComponents/input/upload'
import {
    FeedRefusedError,
    GoogleListingItem,
    RawProductData,
    TicketCandidate,
    Translation,
} from 'channels/googleTTDService'
import { ArticleService, LocalesInfo } from 'admin/articleService'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { ActionButton } from 'uiComponents/buttons'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { ConfirmationDialog } from 'uiComponents/popups/confirmationDialog'
import { useMessages } from 'messagesContext'
import { hideAllMessages, showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { Trans, useTranslation } from 'translations'
import { delay } from 'utils'
import { ListingCard } from './components/listingCard/listingCard'
import { ListedTicket } from './components/ticketsTable/ticketsTable'
import { addedOptions, hasChanges, listingChanges, ListingChanges, removedOptions } from './model/changes'
import { MIN_IMAGE_SIZE } from './model/readiness'
import { htmlToPlainText } from './model/plainText'
import './googleTTD.scss'

interface GoogleTTDPageProps {
    accountSlug: string
    creatingModeOn: boolean
    articleService: ArticleService
    showAddVenueButton: (value: boolean) => void
    onSectionChange: (section: string) => void
    resetCreatingMode: () => void
    /** Hands the page's Save button to the channels frame, which shows it in the tabs row. */
    setPageActions?: (actions: React.ReactNode) => void
}

interface Venue {
    locationId: string
    name: string
    googlePlaceId: string
}

const firstText = (translations: Translation[], preferred: string) =>
    translations.find((translation) => translation.language === preferred)?.text ?? translations[0]?.text ?? ''

const withoutEmptyTexts = (translations: Translation[] = []) =>
    translations.filter((translation) => translation.text.trim().length > 0)

/** Texts saved by the old rich-text page come back as HTML; the page edits plain text. */
const asPlainText = (translations: Translation[] = []) =>
    translations.map((translation) => ({ ...translation, text: htmlToPlainText(translation.text) }))

const flattenTexts = (item: GoogleListingItem): GoogleListingItem => ({
    ...item,
    description: asPlainText(item.description),
    options: item.options.map((option) => ({ ...option, description: asPlainText(option.description) })),
})

/**
 * The Google Things to do channel page: one card per venue with its readiness
 * gate, venue details and tickets, and one Save for the whole page.
 */
function GoogleTTDPage(props: GoogleTTDPageProps) {
    const { t } = useTranslation()
    const googleTTDService = React.useContext(GoogleTTDServiceContext)
    const imagesService = React.useContext(ImagesServiceContext)
    const { removeAllMessages, replaceMessages } = useMessages()
    const isAdmin = useSelector((state: State) => state.auth.user?.isAdmin || false)
    const isPartnerAdmin = useSelector((state: State) => getIsPartnerAdmin(state))
    const canEdit = Boolean(isAdmin || isPartnerAdmin)

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [locales, setLocales] = useState<LocalesInfo | null>(null)
    const [feedArticles, setFeedArticles] = useState<RawProductData[]>([])
    const [candidates, setCandidates] = useState<TicketCandidate[] | null>(null)
    const [saved, setSaved] = useState<GoogleListingItem[]>([])
    const [draft, setDraft] = useState<GoogleListingItem[]>([])
    const [refusals, setRefusals] = useState<Record<string, string>>({})
    const [readiness, setReadiness] = useState<Record<string, boolean>>({})
    const [markErrors, setMarkErrors] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<string>('')

    const languages = locales?.locales ?? []

    const venues: Venue[] = React.useMemo(() => {
        const list: Venue[] = []
        feedArticles.forEach((article) => {
            if (!list.find((venue) => venue.locationId === article.location.id)) {
                list.push({
                    locationId: article.location.id,
                    name: article.location.name,
                    googlePlaceId: article.location.googlePlaceId,
                })
            }
        })
        saved.forEach((item) => {
            if (!list.find((venue) => venue.locationId === item.locationId)) {
                list.push({ locationId: item.locationId, name: item.title, googlePlaceId: item.googlePlaceId })
            }
        })
        return list
    }, [feedArticles, saved])

    const tickets: Record<string, ListedTicket> = React.useMemo(() => {
        const map: Record<string, ListedTicket> = {}
        feedArticles.forEach((article) => {
            const price = article.lowestDayPrice === undefined ? undefined : Number(article.lowestDayPrice)
            map[article.uuid] = {
                name: firstText(article.title, 'en') || t('channels.googleTTD.unknownName'),
                internalId: article.internalId,
                price: price === undefined ? undefined : price > 0 ? price : null,
                currency: article.merchant?.currency ?? 'EUR',
                checkoutDescription: article.description,
            }
        })
        ;(candidates ?? []).forEach((candidate) => {
            if (!map[candidate.uuid]) {
                map[candidate.uuid] = {
                    name: candidate.name,
                    internalId: String(candidate.id),
                    price: undefined,
                    currency: 'EUR',
                    checkoutDescription: [],
                }
            }
        })
        return map
    }, [feedArticles, candidates, t])

    const emptyItem = (venue: Venue): GoogleListingItem => ({
        id: uuid.v4(),
        slug: props.accountSlug,
        locationId: venue.locationId,
        googlePlaceId: venue.googlePlaceId,
        includeInFeed: false,
        title: venue.name,
        image: '',
        description: [],
        options: [],
    })

    async function loadCandidates() {
        try {
            setCandidates(await googleTTDService.getTicketCandidates(props.accountSlug))
        } catch {
            setCandidates([])
        }
    }

    async function load() {
        try {
            const [articles, items, accountLocales] = await Promise.all([
                googleTTDService.getProducts(props.accountSlug),
                googleTTDService.getGoogleTTDItems(props.accountSlug),
                props.articleService.getAccountLocales(props.accountSlug),
            ])
            setFeedArticles(articles)
            setLocales(accountLocales)
            const flattened = items.map(flattenTexts)
            setSaved(flattened)
            setDraft(flattened)
            const venueCount = new Set(articles.map((article) => article.location.id)).size
            props.showAddVenueButton(venueCount > items.length)
        } catch {
            showErrorMessage(t('channels.googleTTD.loadError'))
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        props.onSectionChange('google_ttd')
        setLoading(true)
        load()
        loadCandidates()
    }, [props.accountSlug])

    React.useEffect(() => {
        if (!props.creatingModeOn) return
        const listed = draft.map((item) => item.locationId)
        const venue = venues.find((candidate) => !listed.includes(candidate.locationId))
        if (venue) {
            const next = [...draft, emptyItem(venue)]
            setDraft(next)
            props.showAddVenueButton(venues.length > next.length)
        }
        props.resetCreatingMode()
    }, [props.creatingModeOn])

    const changesByItem = draft.map((item) => ({
        item,
        changes: listingChanges(
            saved.find((savedItem) => savedItem.id === item.id),
            item,
        ),
    }))
    const dirty = changesByItem.filter(({ changes }) => hasChanges(changes))

    const describe = (changes: ListingChanges): string[] => {
        const parts: string[] = []
        if (changes.listingOn) parts.push(t('channels.googleTTD.save.listingOn'))
        if (changes.listingOff) parts.push(t('channels.googleTTD.save.listingOff'))
        if (changes.venue) parts.push(t('channels.googleTTD.save.venue'))
        if (changes.ticketsAdded) parts.push(t('channels.googleTTD.save.ticketsAdded', { count: changes.ticketsAdded }))
        if (changes.ticketsRemoved) {
            parts.push(t('channels.googleTTD.save.ticketsRemoved', { count: changes.ticketsRemoved }))
        }
        if (changes.ticketsChanged) {
            parts.push(t('channels.googleTTD.save.ticketsChanged', { count: changes.ticketsChanged }))
        }
        return parts
    }
    const saveLabel =
        dirty.length === 0
            ? t('channels.googleTTD.save.button')
            : t('channels.googleTTD.save.label', {
                  changes: dirty.flatMap(({ changes }) => describe(changes)).join(', '),
              })

    const saveButton = canEdit ? (
        <DisabledItemHoverInfotip
            active={dirty.length === 0 && !loading}
            infotipText={t('channels.googleTTD.save.nothing')}
            width="14em"
            toTheLeft
        >
            <ActionButton
                kind="action"
                size="medium"
                id="gttd-save"
                disabled={dirty.length === 0 || saving || loading}
                onClick={save}
            >
                {saving ? t('channels.googleTTD.save.saving') : saveLabel}
            </ActionButton>
        </DisabledItemHoverInfotip>
    ) : null

    React.useEffect(() => {
        props.setPageActions?.(saveButton)
    }, [saveLabel, dirty.length, saving, loading, canEdit])

    React.useEffect(() => () => props.setPageActions?.(null), [])

    async function onImageUpload(file: File) {
        const dimensions = await retrieveImgDimentions(file)
        if (!dimensions) {
            return
        }
        if (dimensions.width !== dimensions.height) {
            return Promise.reject({ message: t('channels.googleTTD.imageNotSquare') })
        }
        if (dimensions.width < MIN_IMAGE_SIZE) {
            return Promise.reject({ message: t('channels.googleTTD.imageTooSmall', { size: MIN_IMAGE_SIZE }) })
        }
        return await imagesService.uploadImage(file)
    }

    async function save() {
        const notReady = dirty.filter(({ item }) => item.includeInFeed && readiness[item.id] === false)
        if (notReady.length > 0) {
            setMarkErrors(true)
            showErrorMessage(t('channels.googleTTD.notReady'))
            return
        }
        setSaving(true)
        const nextRefusals: Record<string, string> = {}
        try {
            for (const { item } of dirty) {
                const savedItem = saved.find((candidate) => candidate.id === item.id)
                for (const option of addedOptions(savedItem, item)) {
                    try {
                        await googleTTDService.setTicketInFeed(props.accountSlug, option.articleUUID, true)
                    } catch (error) {
                        nextRefusals[option.articleUUID] = error instanceof FeedRefusedError ? error.code : 'refused'
                    }
                }
                const payload: GoogleListingItem = {
                    ...item,
                    description: withoutEmptyTexts(item.description),
                    options: item.options
                        .filter((option) => !nextRefusals[option.articleUUID])
                        .map((option) => ({ ...option, description: withoutEmptyTexts(option.description) })),
                }
                if (savedItem) {
                    await googleTTDService.updateGoogleTTDItem(props.accountSlug, item.id, payload)
                } else {
                    await googleTTDService.createGoogleTTDItem(props.accountSlug, payload)
                }
                for (const option of removedOptions(savedItem, item)) {
                    await googleTTDService
                        .setTicketInFeed(props.accountSlug, option.articleUUID, false)
                        .catch(() => undefined)
                }
            }
            setRefusals(nextRefusals)
            setMarkErrors(false)
            await load()
            const refusedCount = Object.keys(nextRefusals).length
            showSuccessMessage(
                refusedCount
                    ? t('channels.googleTTD.save.partial', { count: refusedCount })
                    : t('channels.googleTTD.save.success'),
            )
            await delay(4000)
            hideAllMessages()
        } catch {
            showErrorMessage(t('channels.googleTTD.saveError'))
        } finally {
            setSaving(false)
        }
    }

    async function onConfirmDelete() {
        const item = draft.find((candidate) => candidate.id === itemToDelete)
        if (!item) return
        setSaving(true)
        try {
            const savedItem = saved.find((candidate) => candidate.id === item.id)
            if (savedItem) {
                await googleTTDService.deleteGoogleTTDItem(props.accountSlug, item.id)
                for (const option of savedItem.options) {
                    await googleTTDService
                        .setTicketInFeed(props.accountSlug, option.articleUUID, false)
                        .catch(() => undefined)
                }
                await load()
            } else {
                const next = draft.filter((candidate) => candidate.id !== item.id)
                setDraft(next)
                props.showAddVenueButton(venues.length > next.length)
            }
        } catch {
            showErrorMessage(t('channels.googleTTD.deleteError'))
        } finally {
            setSaving(false)
            setItemToDelete('')
        }
    }

    const listedLocations = draft.map((item) => item.locationId)

    return (
        <div className="gttd-page">
            {!!itemToDelete && (
                <ConfirmationDialog
                    title={t('channels.googleTTD.deleteConfirmTitle')}
                    text={t('channels.googleTTD.deleteConfirmText')}
                    buttonText={t('common.delete')}
                    destructive
                    onCancel={() => setItemToDelete('')}
                    onConfirm={onConfirmDelete}
                    loading={saving}
                />
            )}
            <div className="gttd-toolbar">
                <div className="gttd-intro">
                    <h1 className="gttd-title">{t('channels.googleTTD.title')}</h1>
                    <p className="gttd-intro-text">{t('channels.googleTTD.intro')}</p>
                    <p className="gttd-intro-text">
                        <Trans
                            i18nKey="channels.googleTTD.moreInfo"
                            components={{
                                anchor: (
                                    <a
                                        className="gttd-link"
                                        target="_blank"
                                        rel="noreferrer"
                                        href="https://support.convious.com/help/google-things-to-do-free-organic-search-listing"
                                    />
                                ),
                            }}
                        />
                    </p>
                </div>
            </div>
            {loading && (
                <div className="listing-card" aria-busy="true">
                    <SkeletonBar width="40%" height="1.25rem" />
                    <SkeletonBar width="100%" height="4rem" />
                    <SkeletonBar width="100%" height="12rem" />
                </div>
            )}
            {!loading && draft.length === 0 && venues.length === 0 && (
                <p className="gttd-empty">{t('channels.googleTTD.noProducts')}</p>
            )}
            {!loading &&
                draft.map((item) => {
                    const venue = venues.find((candidate) => candidate.locationId === item.locationId)
                    const venueOptions = venues
                        .filter(
                            (candidate) =>
                                !listedLocations.includes(candidate.locationId) ||
                                candidate.locationId === item.locationId,
                        )
                        .map((candidate) => ({ name: candidate.name, value: candidate.locationId }))
                    return (
                        <ListingCard
                            key={item.id}
                            accountSlug={props.accountSlug}
                            articleService={props.articleService}
                            item={item}
                            saved={saved.find((candidate) => candidate.id === item.id)}
                            venueName={venue?.name ?? item.title}
                            venueOptions={venueOptions}
                            languages={languages}
                            tickets={tickets}
                            candidates={candidates ?? []}
                            candidatesLoading={candidates === null}
                            refusals={refusals}
                            markErrors={markErrors}
                            canEdit={canEdit && !saving}
                            onChange={(next) =>
                                setDraft(draft.map((candidate) => (candidate.id === next.id ? next : candidate)))
                            }
                            onReadinessChange={(ready) => setReadiness((current) => ({ ...current, [item.id]: ready }))}
                            onDelete={() => setItemToDelete(item.id)}
                            onImageUpload={onImageUpload}
                            replaceMessages={replaceMessages}
                            removeAllMessages={removeAllMessages}
                        />
                    )
                })}
        </div>
    )
}

export default GoogleTTDPage
